import React, { PropsWithChildren, useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector, useTranslate } from '@src/core/hooks';
import ProductAdderTariffVerificationField from './ProductAdderTariffVerificationField';
import { useTariffVerificationHandlers } from '@src/domains/products/features/productAdder/hooks/useTariffVerificationHandlers';
import { TariffVerificationFieldsProps } from '@src/domains/products/features/productAdder/types/tariffVerification';
import {
  loadingTariffVerificationsSelector,
  tariffVerificationsSelector,
  tariffVerificationValuesSelector,
  loadTariffVerifications,
  updateTariffVerificationValues,
} from '@src/stores/product';
import { cleanExcessVerificationValues } from '@src/domains/tariffs/core/tariffVerificationFields/cleanExcessVerificationValues';
import { filterRenderableVerifications } from '@src/domains/tariffs/core/tariffVerificationFields/filterRenderableVerifications';
import { getVerificationValue } from '@src/domains/tariffs/core/tariffVerificationFields/getVerificationValue';
import {
  VerificationBinCodeHelperText,
  VerificationFieldsContainer,
  VerificationLoadingText,
  VerificationPersonContainer,
} from './styles';
import { TariffVerificationTypeEnum } from '@src/core/buyerApiClient';
import { addCartItemAddedSelector } from '@src/features/cart/store/cart';
import { useWidgetId } from '@src/core/IdProvider';
import useSelectedProductId from '@src/hooks/useSelectedProductId';
import { getCartTariffVerificationError } from '@src/domains/tariffs/core/tariffVerificationFields/getCartTariffVerificationError';
import { getFieldError } from '@src/domains/tariffs/core/tariffVerificationFields/getFieldError';

const ProductAdderTariffVerificationFields: React.FC<PropsWithChildren<TariffVerificationFieldsProps>> = ({
  tariffTypeId,
  quantity,
  tariffTypePosition,
}) => {
  const { i18n } = useTranslate();
  const widgetId = useWidgetId();
  const productId = useSelectedProductId();
  const dispatch = useAppDispatch();

  // Selectors
  const tariffTypeVerifications = useAppSelector(tariffVerificationsSelector(tariffTypeId));
  const loading = useAppSelector(loadingTariffVerificationsSelector(tariffTypeId));
  const tariffWithVerificationValues = useAppSelector(tariffVerificationValuesSelector(widgetId, productId));
  const { errors: cartErrors } = useAppSelector(addCartItemAddedSelector);

  const arrayOfQuantity = useMemo(() => Array.from({ length: quantity }), [quantity]);

  const verificationValuesWithoutUnrendered = useMemo(
    () => filterRenderableVerifications(tariffTypeVerifications),
    [tariffTypeVerifications],
  );

  const binCodeVerifications = useMemo(() => {
    return tariffTypeVerifications.filter(
      (verification) => verification.tariffVerification.type === TariffVerificationTypeEnum.BinCode,
    );
  }, [tariffTypeVerifications]);

  const tariffHasValidationsToRender = verificationValuesWithoutUnrendered.length > 0;

  useEffect(() => {
    if (quantity > 0 && tariffTypeId) {
      dispatch(loadTariffVerifications(tariffTypeId));
    }
  }, [tariffTypeId, quantity, dispatch]);

  useEffect(() => {
    if (tariffWithVerificationValues && tariffTypeId) {
      const { updatePayload } = cleanExcessVerificationValues(tariffWithVerificationValues, tariffTypeId, quantity);

      if (updatePayload) {
        dispatch(updateTariffVerificationValues(widgetId, productId, updatePayload));
      }
    }
  }, [quantity, tariffWithVerificationValues, tariffTypeId, dispatch, widgetId, productId]);

  // Handlers management
  const { handleValueChange } = useTariffVerificationHandlers(tariffTypeId);

  if (loading) {
    return (
      <VerificationFieldsContainer>
        <VerificationLoadingText>{i18n('base_loading')}</VerificationLoadingText>
      </VerificationFieldsContainer>
    );
  }

  return (
    <VerificationFieldsContainer>
      {binCodeVerifications.map((binCodeVerification) =>
        binCodeVerification.tariffVerification.helperText ? (
          <VerificationBinCodeHelperText key={binCodeVerification.id}>
            {binCodeVerification.tariffVerification.helperText}
          </VerificationBinCodeHelperText>
        ) : null,
      )}
      {tariffHasValidationsToRender
        ? arrayOfQuantity.map((_, tariffUnitIndex) => (
            <VerificationPersonContainer key={tariffUnitIndex}>
              {verificationValuesWithoutUnrendered.map((tariffTypeVerification, verificationIndex) => (
                <ProductAdderTariffVerificationField
                  key={`${tariffTypeVerification.id}-${tariffUnitIndex}`}
                  tariffTypeVerification={tariffTypeVerification}
                  tariffUnitIndex={tariffUnitIndex}
                  initialValue={getVerificationValue(
                    tariffWithVerificationValues,
                    tariffTypeId,
                    tariffTypeVerification.tariffVerificationId,
                    tariffUnitIndex,
                    verificationIndex,
                  )}
                  getFieldError={(verificationId) =>
                    getFieldError(tariffWithVerificationValues, verificationId, tariffUnitIndex, verificationIndex)
                  }
                  getCartTariffVerificationError={() =>
                    getCartTariffVerificationError(
                      cartErrors ?? {},
                      tariffUnitIndex,
                      verificationIndex,
                      verificationValuesWithoutUnrendered.length,
                      tariffTypePosition ?? 0,
                    )
                  }
                  handleValueChange={(verificationId, value) =>
                    handleValueChange(verificationId, value, tariffUnitIndex, verificationIndex)
                  }
                />
              ))}
            </VerificationPersonContainer>
          ))
        : null}
    </VerificationFieldsContainer>
  );
};

export default ProductAdderTariffVerificationFields;
